import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FormLayoutElement } from "@/config/forms/general";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { BibliographyInBibliography, Bibliography } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { BibliographyLinkGridCard } from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import BibliographyInBibliographyDrawer from "./drawer/BibliographyInBibliographyDrawer";

/**
 * Lien complexe auto-référentiel Biblio ↔ Biblio.
 * ⚠️ WIP / champs PLACEHOLDER — version fonctionnelle allégée (liste + ajout +
 * suppression + édition via drawer). À aligner sur DocumentInBibliography (styles/
 * colonnes) une fois les champs de la table de transition finalisés.
 */
const BibliographyInBibliographyLinks = ({
  id,
  type,
  fields,
}: {
  id: string;
  type: LinkableType;
  fields: FormLayoutElement[] | undefined;
}) => {
  const { t } = useTranslation();

  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "bibliographyInBibliography",
    type,
  );
  const { mutate: deleteBibliographyInBibliography } = useDeleteComplexLink(
    id,
    "bibliographyInBibliography",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [searchContent, setSearchContent] = useState("");
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [addPagination, setAddPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  // Gestion des colonnes (visibilité + persistance), comme les autres liens.
  const STORAGE_KEY_LINKED = "bibliography-in-bibliography-linked";
  const STORAGE_KEY_ADD = "bibliography-in-bibliography-add";
  const defaultVisibility: VisibilityState = {
    ...bibliographyColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved?.visibility && typeof saved.visibility === "object")
          return { ...defaultVisibility, ...saved.visibility };
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved?.visibility && typeof saved.visibility === "object")
          return { ...defaultVisibility, ...saved.visibility };
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });

  const handleDeleteRelation = useCallback(
    (relationId: string) => {
      deleteBibliographyInBibliography({
        id: relationId,
        relation: "bibliographyInBibliography",
      });
    },
    [deleteBibliographyInBibliography],
  );

  const { data: notRelated } = useGetNotRelatedResource<Bibliography>({
    resultType: "bibliography",
    resourceId: id,
    pagination: addPagination,
    searchQuery: searchContent,
    associationScope: "full",
  });

  // Auto-exclusion de la fiche courante (en attendant l'exclusion serveur).
  const notRelatedBibliographies = useMemo(
    () => (notRelated?.data ?? []).filter((b) => b.id !== id),
    [notRelated?.data, id],
  );

  const previewElements = useCallback(
    (biblio: Bibliography) => [
      {
        label: t("bibliographieDescriptionNumeroBibliographie"),
        value: biblio.bibliographieDescriptionNumeroBibliographie,
        type: "text" as const,
      },
      {
        label: t("bibliographieDescriptionResumeLong"),
        value: biblio.bibliographieDescriptionResumeLong,
        type: "richtext" as const,
      },
      {
        label: t("attachments"),
        value: biblio.attachments,
        type: "image" as const,
      },
    ],
    [t],
  );

  // Colonnes de la liste des liens existants.
  const linkedColumns: ColumnDef<BibliographyInBibliography, any>[] = useMemo(
    () => [
      {
        accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        size: 120,
        cell: (props: any) => <TextCell value={props.getValue()} />,
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 80,
        cell: ({ row }: { row: Row<BibliographyInBibliography> }) => (
          <ImageCell
            value=""
            attachments={row.original.bibliography?.attachments ?? []}
            domain="bibliography"
          />
        ),
      },
      {
        accessorKey: "bibliography.bibliographieDescriptionResumeLong",
        header: t("bibliographieDescriptionResumeLong"),
        cell: (props: any) => <HtmlCell value={props.getValue()} />,
      },
      {
        accessorKey: "bibliographieDansBibliographieDescriptionReproduitPage",
        header: t("bibliographieDansBibliographieDescriptionReproduitPage"),
      },
      {
        accessorKey: "bibliographieDansBibliographieDescriptionCitePage",
        header: t("bibliographieDansBibliographieDescriptionCitePage"),
      },
      createActionsColumnDef<BibliographyInBibliography>(({ row }) => {
        const data = row.original as BibliographyInBibliography;
        return (
          <LinkDrawerRowActions
            previewType="bibliography"
            previewId={data.bibliography.id}
            previewElements={previewElements(data.bibliography)}
            onDelete={() => handleDeleteRelation(data.id)}
            editDrawer={({ open, onOpenChange }) => (
              <BibliographyInBibliographyDrawer
                key={data.id}
                fields={fields}
                queryId={id}
                type={type}
                data={data}
                open={open}
                onOpenChange={onOpenChange}
              />
            )}
          />
        );
      }),
    ],
    [t, previewElements, handleDeleteRelation, fields, id, type],
  );

  // Colonnes de la liste "Ajouter" (biblios non liées).
  const addColumns: ColumnDef<Bibliography, any>[] = useMemo(
    () => [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        size: 120,
        cell: (props: any) => <TextCell value={props.getValue()} />,
      },
      {
        accessorKey: "attachments",
        header: "",
        size: 80,
        cell: ({ row }: { row: Row<Bibliography> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="bibliography"
          />
        ),
      },
      {
        accessorKey: "bibliographieDescriptionResumeLong",
        header: t("bibliographieDescriptionResumeLong"),
        cell: (props: any) => <HtmlCell value={props.getValue()} />,
      },
      createActionsColumnDef<Bibliography>(({ row }) => {
        const biblio = row.original as Bibliography;
        return (
          <LinkDrawerRowActions
            previewType="bibliography"
            previewId={biblio.id}
            previewElements={previewElements(biblio)}
            editLabel={t("actions.add")}
            editDrawer={({ open, onOpenChange }) => (
              <BibliographyInBibliographyDrawer
                key={biblio.id}
                fields={fields}
                queryId={id}
                type={type}
                open={open}
                onOpenChange={onOpenChange}
                data={{
                  id: "",
                  bibliography: biblio,
                  bibliographieDansBibliographieDescriptionPage: "",
                  bibliographieDansBibliographieDescriptionReproduitPage: "",
                  bibliographieDansBibliographieDescriptionCitePage: "",
                  bibliographieDansBibliographieDescriptionListePage: "",
                  bibliographieDansBibliographieDescriptionNonPagine: false,
                  bibliographieDansBibliographieDescriptionReproductionCouverture:
                    false,
                  bibliographieDansBibliographieDescriptionReproductionCouleur:
                    false,
                  bibliographieDansBibliographieDescriptionTextePersonnalise: "",
                }}
              />
            )}
          />
        );
      }),
    ],
    [t, previewElements, fields, id, type],
  );

  const searchInput = (
    <Input
      placeholder={t("search")}
      value={searchContent}
      onChange={(e) => setSearchContent(e.target.value)}
    />
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, start + relatedPagination.pageSize);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey="bibliography-in-bibliography-links"
      title={t("entrybibliographies")}
      isAddMode={isAddMode}
      onBack={() => setIsAddMode(false)}
      onToggleAdd={() => setIsAddMode(true)}
      searchInput={searchInput}
      relatedPagination={relatedPaginationProps}
      addModeTable={
        <DataTable
          columns={addColumns}
          data={notRelatedBibliographies}
          storageKey={STORAGE_KEY_ADD}
          pagination={{
            pagination: addPagination,
            setPage: setAddPagination,
            totalSize: notRelated?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibilityAdd,
            state: { columnVisibility: columnVisibilityAdd },
          }}
        />
      }
      listTable={
        <DataTable
          columns={linkedColumns}
          data={paginatedRelatedData}
          storageKey={STORAGE_KEY_LINKED}
          pagination={relatedPaginationProps}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibilityLinked,
            state: { columnVisibility: columnVisibilityLinked },
          }}
        />
      }
      gridContent={
        <>
          {paginatedRelatedData.map((row: BibliographyInBibliography) => (
            <div key={row.id}>
              <GridCardWithActions>
                <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
              </GridCardWithActions>
              <BibliographyInBibliographyDrawer
                fields={fields}
                data={row}
                queryId={id}
                type={type}
                open={selectedRelationId === row.id}
                onOpenChange={(open) => {
                  if (!open) setSelectedRelationId(null);
                }}
              />
            </div>
          ))}
        </>
      }
    />
  );
};

export default BibliographyInBibliographyLinks;
